import {put, select} from "../../../../shared/SagaService";
import ContactActions from 'bukku/redux/Contact/actions';
import actions from "../../actions";
import EditableActions from "../../../EditableTable/actions";
import {TableName} from "../../../EditableTable/options";
import {Type} from "../../helper";
import {PageType} from "../../../../enums/PageType";

const {
    setAmountDefined,
    changeAmount,
    setShowShipping,
    setRounding,
    transactionSetAppliedLinkedIds,
    changeLinkItems,
    transactionChangeStatus,
    transactionChangeMyinvoisAction,
} = actions;

const {
    setBillingContactPersonId,
    setShippingContactPersonId,
} = ContactActions;

// helper
function* setItems(name, items, isVoid) {
    yield put(EditableActions.setItems(name, items, isVoid));
}

// main function
export function* updateStore(transaction, transactionType, transactionInitCase, restoreForm) {
    const settings = yield select(state => state.List.settings);
    const company = yield select(state => state.Company.company);
    const isVoid = transaction.status === 'void';

    yield put(
        actions.setSendEmail(settings.transaction_email_master_on),
    );

    // Editable Tables:
    switch (transactionType) {
        case Type.sale_quote:
        case Type.sale_order:
        case Type.purchase_order:
            yield setItems(TableName.form_item, transaction.form_items || [], isVoid);
            yield setItems(TableName.payment_term, transaction.term_items || [], isVoid);
            break;
        case Type.sale_delivery_order:
        case Type.sale_credit_note:
        case Type.purchase_goods_received_note:
        case Type.purchase_credit_note:
            yield setItems(TableName.form_item, transaction.form_items || [], isVoid);
            break;
        case Type.sale_payment:
        case Type.sale_refund:
        case Type.purchase_payment:
        case Type.purchase_refund:
            yield setItems(TableName.form_item, transaction.form_items || [], isVoid);
            yield setItems(TableName.payment_deposit_item, transaction.deposit_items || [], isVoid);
            break;
        case Type.sale_invoice:
            yield setItems(TableName.form_item, transaction.form_items || [], isVoid);
            yield setItems(TableName.payment_term, transaction.term_items || [], isVoid);
            yield setItems(TableName.payment_deposit_item, transaction.deposit_items || [], isVoid);
            break;
        case Type.purchase_bill:
            yield setItems(TableName.form_item, transaction.form_items || [], isVoid);
            yield setItems(TableName.payment_term, transaction.term_items || [], isVoid);
            yield setItems(TableName.payment_deposit_item, transaction.deposit_items || [], isVoid);
            break;
        case Type.bank_income:
        case Type.bank_expense:
            yield setItems(TableName.bank_item, transaction.bank_items, isVoid);
            yield setItems(TableName.payment_deposit_item, transaction.deposit_items || [], isVoid);
            break;
        case Type.journal_entry:
            yield setItems(TableName.journal_item, transaction.journal_items, isVoid);
            break;
        case Type.stock_adjustment:
            yield setItems(TableName.stock_adjustment, transaction.items, isVoid);
            break;
        case Type.stock_transfer:
            yield setItems(TableName.stock_transfer, transaction.items, isVoid);
            break;
        case Type.stock_value:
            yield setItems(TableName.stock_values, transaction.accounts.map(account => ({
                ...account,
                months: account.months.map(m => ({...m})),
            })), isVoid);
            break;
        default:
            break;
    }

    // Set Billing and Shipping contact persons
    switch (transactionType) {
        case Type.sale_quote:
        case Type.sale_delivery_order:
        case Type.sale_order:
        case Type.sale_invoice:
        case Type.sale_credit_note:
        case Type.purchase_order:
        case Type.purchase_goods_received_note:
        case Type.purchase_bill:
        case Type.purchase_credit_note:
            yield put(setBillingContactPersonId(transaction.billing_contact_person_id));
            yield put(setShippingContactPersonId(transaction.shipping_contact_person_id));
            break;
        case Type.sale_payment:
        case Type.sale_refund:
        case Type.purchase_payment:
        case Type.purchase_refund:
        case Type.bank_income:
        case Type.bank_expense:
            yield put(setBillingContactPersonId(transaction.billing_contact_person_id));
            break;
        default:
            break;
    }

    // Extra
    switch (transactionType) {
        case Type.sale_quote:
        case Type.sale_order:
        case Type.sale_delivery_order:
        case Type.sale_credit_note:
        case Type.sale_invoice:
            yield put(setAmountDefined(transactionInitCase !== PageType.new));
            if (transactionInitCase !== PageType.new || restoreForm)
                yield put(setRounding(transaction.rounding_on));
            else if ([Type.sale_invoice].includes(transactionType))
                yield put(setRounding(settings.default_rounding_on));
            break;
        case Type.sale_payment:
        case Type.sale_refund:
            yield put(setAmountDefined(transactionInitCase !== PageType.new));
            break;
        case Type.purchase_order:
        case Type.purchase_goods_received_note:
        case Type.purchase_bill:
        case Type.purchase_credit_note:
        case Type.purchase_payment:
        case Type.purchase_refund: {
            yield put(setAmountDefined(transactionInitCase !== PageType.new));
            if (transaction.amount)
                yield put(changeAmount(transaction.amount));
            if (company.shipping_address_text)
                yield put(setShowShipping(true));

            if (![Type.purchase_payment, Type.purchase_refund].includes(transactionType)) {
                if (transactionInitCase !== PageType.new || restoreForm)
                    yield put(setRounding(transaction.rounding_on));
            }
            break;
        }
        case Type.bank_income:
        case Type.bank_expense:
            if (transactionInitCase !== PageType.new || restoreForm)
                yield put(setRounding(transaction.rounding_on));

            break;
        default:
            break;
    }

    if (
        [Type.sale_invoice, Type.purchase_bill].includes(transactionType) &&
        (transactionInitCase === PageType.view || restoreForm)
    ) {
        yield put(
            transactionSetAppliedLinkedIds(
                transaction?.linked_items?.map(i => i.origin_transaction_id) || [],
            ),
        );
        yield put(
            changeLinkItems(
                (transaction?.linked_items || []).map(i => ({
                    ...i,
                    target_transaction_id: i.origin_transaction_id,
                })),
            ),
        );
    }

    yield put(
        transactionChangeStatus(transaction.status),
    );
    yield put(
        transactionChangeMyinvoisAction(transaction.myinvois_action),
    );

    yield put(
        actions.transactionInitSuccess(transaction, transactionType),
    );

    if (restoreForm) {
        // this has to be after transactionInitSuccess, to prevent breaking deemed payment panelLinks.js componentDidUpdate part
        yield put({
            type: actions.TRANSACTION_RESTORE_AUTOSAVE_DATA_SUCCESS,
        });
    }
}
